import { create } from 'zustand'

export type Theme = 'light' | 'dark'
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

const THEME_STORAGE_KEY = 'app-theme'

/** Read persisted theme from localStorage, falling back to system preference. */
function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch { /* localStorage unavailable */ }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light'
  }
  return 'dark'
}

interface AppState {
  // Theme
  theme: Theme
  setTheme: (theme: Theme) => void

  // Connection status to RAG server
  ragConnectionStatus: ConnectionStatus
  setRagConnectionStatus: (status: ConnectionStatus) => void

  // Active persona ID
  activePersonaId: string | null
  setActivePersonaId: (id: string | null) => void

  // UI state
  isSidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void

  // Global loading states
  isInitializing: boolean
  setInitializing: (loading: boolean) => void

  // Backend sync: populate from backend preferences
  loadPreferences: (data: {
    theme?: Theme
    activePersonaId?: string | null
    isSidebarCollapsed?: boolean
  }) => void

  // Get preferences for syncing to backend
  getPreferencesForSync: () => {
    theme: Theme
    activePersonaId: string | null
    isSidebarCollapsed: boolean
  }
}

export const useAppStore = create<AppState>()(
  (set, get) => ({
    // Theme - persisted via localStorage for instant restore
    theme: getInitialTheme(),
    setTheme: (theme) => {
      try { localStorage.setItem(THEME_STORAGE_KEY, theme) } catch { /* noop */ }
      set({ theme })
    },

    // Connection status
    ragConnectionStatus: 'disconnected',
    setRagConnectionStatus: (status) => set({ ragConnectionStatus: status }),

    // Active persona
    activePersonaId: null,
    setActivePersonaId: (id) => set({ activePersonaId: id }),

    // UI state
    isSidebarCollapsed: false,
    setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),

    // Loading states
    isInitializing: true,
    setInitializing: (loading) => set({ isInitializing: loading }),

    // Backend sync
    loadPreferences: (data) => {
      // Theme: localStorage is the immediate source of truth (written on every
      // toggle). Only adopt the backend theme when localStorage has no value
      // (new device / cleared storage), so a stale backend value from before
      // the debounced sync can't overwrite a recent user choice.
      let applyBackendTheme = false
      if (data.theme !== undefined) {
        try {
          const local = localStorage.getItem(THEME_STORAGE_KEY)
          if (!local) {
            // No local preference — adopt backend value and cache it
            localStorage.setItem(THEME_STORAGE_KEY, data.theme)
            applyBackendTheme = true
          }
        } catch {
          // Can't read localStorage — fall back to backend value
          applyBackendTheme = true
        }
      }
      set({
        ...(applyBackendTheme && { theme: data.theme }),
        ...(data.activePersonaId !== undefined && { activePersonaId: data.activePersonaId }),
        ...(data.isSidebarCollapsed !== undefined && { isSidebarCollapsed: data.isSidebarCollapsed }),
      })
    },

    getPreferencesForSync: () => {
      const { theme, activePersonaId, isSidebarCollapsed } = get()
      return { theme, activePersonaId, isSidebarCollapsed }
    },
  })
)
